import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js'

import { isSupabaseConfigured, supabase } from '@/lib/supabase'
import { formatSupabaseAuthError } from '@/lib/supabase-auth-errors'
import type { Profile } from '@/types/health'

interface SignUpPayload {
  email: string
  password: string
  fullName: string
}

interface AuthContextValue {
  session: Session | null
  user: User | null
  profile: Profile | null
  loading: boolean
  isDemo: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (payload: SignUpPayload) => Promise<{ needsEmailConfirmation: boolean }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AUTH_PROFILE_CACHE_KEY = 'medtrack:auth:profile'

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function readCachedProfile(): Profile | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const rawValue = window.localStorage.getItem(AUTH_PROFILE_CACHE_KEY)

    if (!rawValue) {
      return null
    }

    const parsed = JSON.parse(rawValue) as Partial<Profile> | null

    if (!parsed || typeof parsed !== 'object') {
      return null
    }

    if (
      typeof parsed.id !== 'string' ||
      typeof parsed.email !== 'string' ||
      (parsed.role !== 'patient' && parsed.role !== 'doctor' && parsed.role !== 'admin')
    ) {
      return null
    }

    return parsed as Profile
  } catch {
    return null
  }
}

function writeCachedProfile(profile: Profile | null) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    if (profile) {
      window.localStorage.setItem(AUTH_PROFILE_CACHE_KEY, JSON.stringify(profile))
    } else {
      window.localStorage.removeItem(AUTH_PROFILE_CACHE_KEY)
    }
  } catch {
    // Ignore localStorage write failures.
  }
}

const buildFallbackProfile = (user: User): Profile => {
  const now = new Date().toISOString()
  const metadataRole = user.user_metadata?.role
  const safeRole =
    metadataRole === 'doctor' || metadataRole === 'admin' || metadataRole === 'patient'
      ? metadataRole
      : 'patient'

  return {
    id: user.id,
    email: user.email ?? '',
    full_name:
      typeof user.user_metadata?.full_name === 'string' ? user.user_metadata.full_name : null,
    role: safeRole,
    created_at: now,
    updated_at: now
  }
}

function shouldSyncRole(currentRole: Profile['role'], expectedRole: Profile['role']) {
  if (currentRole === expectedRole) {
    return false
  }

  return expectedRole === 'doctor' || expectedRole === 'admin'
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(() => readCachedProfile())
  const [loading, setLoading] = useState(true)
  const isMountedRef = useRef(true)
  const profileRequestIdRef = useRef(0)

  function commitProfile(nextProfile: Profile | null) {
    if (!isMountedRef.current) {
      return
    }

    setProfile(nextProfile)
    writeCachedProfile(nextProfile)
  }

  async function loadProfile(nextUser: User): Promise<Profile> {
    const requestId = ++profileRequestIdRef.current
    const fallbackProfile = buildFallbackProfile(nextUser)

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', nextUser.id)
        .maybeSingle()

      if (requestId !== profileRequestIdRef.current || !isMountedRef.current) {
        return fallbackProfile
      }

      if (error) {
        console.error('Impossible de charger le profil Supabase:', error.message)
        commitProfile(fallbackProfile)
        return fallbackProfile
      }

      if (data) {
        const existingProfile = data as Profile

        if (shouldSyncRole(existingProfile.role, fallbackProfile.role)) {
          const syncedPayload: Profile = {
            ...existingProfile,
            email: fallbackProfile.email || existingProfile.email,
            full_name: fallbackProfile.full_name || existingProfile.full_name,
            role: fallbackProfile.role
          }

          const { data: syncedProfile, error: syncError } = await supabase
            .from('profiles')
            .upsert(syncedPayload)
            .select('*')
            .single()

          if (
            requestId === profileRequestIdRef.current &&
            isMountedRef.current &&
            !syncError &&
            syncedProfile
          ) {
            commitProfile(syncedProfile as Profile)
            return syncedProfile as Profile
          }
        }

        commitProfile(existingProfile)
        return existingProfile
      }

      const { data: createdProfile, error: createError } = await supabase
        .from('profiles')
        .upsert(fallbackProfile)
        .select('*')
        .single()

      if (requestId !== profileRequestIdRef.current || !isMountedRef.current) {
        return fallbackProfile
      }

      if (createError) {
        console.error('Impossible de creer le profil Supabase:', createError.message)
        commitProfile(fallbackProfile)
        return fallbackProfile
      }

      commitProfile(createdProfile as Profile)
      return createdProfile as Profile
    } catch (error) {
      if (requestId !== profileRequestIdRef.current || !isMountedRef.current) {
        return fallbackProfile
      }

      console.error('Erreur inattendue lors du chargement du profil:', error)
      commitProfile(fallbackProfile)
      return fallbackProfile
    }
  }

  async function applySession(nextSession: Session | null, options?: { awaitProfile?: boolean }) {
    if (!isMountedRef.current) {
      return
    }

    const nextUser = nextSession?.user ?? null
    setSession(nextSession)
    setUser(nextUser)

    if (!nextUser) {
      profileRequestIdRef.current += 1
      commitProfile(null)
      return
    }

    const fallbackProfile = buildFallbackProfile(nextUser)
    if (!profile || profile.id !== nextUser.id) {
      commitProfile(fallbackProfile)
    }

    const profilePromise = loadProfile(nextUser)

    if (options?.awaitProfile) {
      await profilePromise
    }
  }

  async function rehydrateSession(options?: { preserveLoading?: boolean }) {
    const preserveLoading = options?.preserveLoading ?? true

    if (!preserveLoading && isMountedRef.current) {
      setLoading(true)
    }

    try {
      const { data, error } = await supabase.auth.getSession()

      if (error) {
        throw error
      }

      await applySession(data.session ?? null, { awaitProfile: false })
    } catch (error) {
      console.error('Impossible de restaurer la session Supabase:', error)
      await applySession(null, { awaitProfile: false })
    } finally {
      if (!preserveLoading && isMountedRef.current) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    isMountedRef.current = true

    async function bootstrapAuth() {
      try {
        const { data, error } = await supabase.auth.getSession()

        if (error) {
          throw error
        }

        await applySession(data.session ?? null, { awaitProfile: false })
      } catch (error) {
        console.error('Bootstrap auth impossible:', error)
        await applySession(null, { awaitProfile: false })
      } finally {
        if (isMountedRef.current) {
          setLoading(false)
        }
      }
    }

    void bootstrapAuth()

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(
      (_event: AuthChangeEvent, nextSession: Session | null) => {
        void applySession(nextSession)

        if (isMountedRef.current) {
          setLoading(false)
        }
      }
    )

    const handleSessionResume = () => {
      void rehydrateSession({ preserveLoading: true })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleSessionResume()
      }
    }

    window.addEventListener('focus', handleSessionResume)
    window.addEventListener('online', handleSessionResume)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      isMountedRef.current = false
      subscription.unsubscribe()
      window.removeEventListener('focus', handleSessionResume)
      window.removeEventListener('online', handleSessionResume)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  async function signIn(email: string, password: string) {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })

      if (error) {
        throw error
      }
    } catch (error) {
      throw new Error(formatSupabaseAuthError(error, { operation: 'signin' }))
    }
  }

  async function signUp({ email, password, fullName }: SignUpPayload) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'patient'
          }
        }
      })

      if (error) {
        throw error
      }

      if (data.user) {
        await loadProfile(data.user)
      }

      return {
        needsEmailConfirmation: !data.session
      }
    } catch (error) {
      throw new Error(formatSupabaseAuthError(error, { operation: 'signup' }))
    }
  }

  async function signOut() {
    try {
      const { error } = await supabase.auth.signOut()

      if (error) {
        throw error
      }
    } catch (error) {
      throw new Error(formatSupabaseAuthError(error, { operation: 'signout' }))
    }
  }

  async function refreshProfile() {
    if (!user) {
      return
    }

    await loadProfile(user)
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        loading,
        isDemo: !isSupabaseConfigured,
        signIn,
        signUp,
        signOut,
        refreshProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth doit etre utilise dans AuthProvider')
  }

  return context
}
